import React, { useCallback, useState } from 'react'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Button } from '@app/components/general/buttons/button'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { PriceTotal } from '../general/payments/total'
import { AppManager } from '@app/managers'
import { getSubmitStyles } from './submit-btn'
import { DOMAIN_NAME } from '@app/configs'
import { useRouter } from 'next/router'
import { useAuthContext } from '../providers/auth'
import { LocalStorageKeys } from '@app/configs/app-config'

interface Props {
  onToken(token: any): any
  plan?: string
  price: number
  disabled: boolean
  downgrade?: boolean
  addCredits?: boolean
  credits?: number
  ephemiral?: string
  // used to base the USD far or less.
  __convertor?: number
}

const confirmParams = {
  return_url: `${DOMAIN_NAME}/payments/complete`,
}

// Stripe checkout form.
export const CheckoutForm = ({
  // onToken,
  price,
  disabled,
  downgrade,
  addCredits,
  credits,
  ephemiral,
  __convertor,
}: Props) => {
  const { t, lang } = useTranslation('payments')
  const router = useRouter()
  const [loading, setLoading] = useState<boolean>(false)
  const [complete, setCompleted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  useAuthContext()

  const stripe = useStripe()
  const elements = useElements()

  // submit action for payment
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (!stripe || !elements || loading) {
        return
      }

      // update the price depending on what is picked.
      await elements.fetchUpdates()

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit()

      if (submitError) {
        return setErrorMessage(submitError.message)
      }

      if (errorMessage) {
        setErrorMessage('')
      }

      const cardElement = elements.getElement(PaymentElement)

      if (cardElement) {
        setLoading(true)

        let error = null

        try {
          localStorage.setItem(LocalStorageKeys.ReloadRequired, 'true')
        } catch (e) {
          console.error(e)
        }

        try {
          const { error: stripeError } = ephemiral
            ? await stripe.confirmSetup({
                elements,
                confirmParams,
              })
            : await stripe.confirmPayment({
                elements,
                confirmParams,
              })
          error = stripeError
        } catch (e) {
          console.error(e)
        }

        if (error) {
          if (error && 'message' in error) {
            AppManager.toggleSnack(true, error.message, 'error')
          } else if (typeof error === 'string') {
            AppManager.toggleSnack(true, error, 'error')
          }
        } else {
          setTimeout(async () => {
            await router.push('/dashboard')
          }, 2500)
        }

        setLoading(false)
      }
    },
    [
      ephemiral,
      router,
      setLoading,
      elements,
      stripe,
      loading,
      setErrorMessage,
      errorMessage,
    ]
  )

  const onChangeText = useCallback(
    (e: { complete?: boolean }) => setCompleted(!!e?.complete),
    [setCompleted]
  )

  return (
    <form
      onSubmit={handleSubmit}
      className={'space-y-3 border-t dark:border-gray-800 pt-4 pb-3 w-full'}
    >
      <div className='space-y-3'>
        <PaymentElement
          onChange={onChangeText}
          id='payment-element'
          options={{
            layout: 'tabs',
          }}
          className={'p-4 border-2 rounded dark:border-gray-600'}
        />
        <Button
          type='submit'
          disabled={!complete || !stripe || disabled || loading}
          border={false}
          round={false}
          className={getSubmitStyles(
            addCredits && credits ? true : complete && !disabled
          )}
        >
          {t(addCredits ? 'purchase' : downgrade ? 'downgrade' : 'upgrade')}{' '}
          <PriceTotal
            price={__convertor ? __convertor * price : price}
            lang={__convertor && __convertor !== 1 ? lang : undefined}
            noui
          />
        </Button>

        {errorMessage && <div className='py-2'>{errorMessage}</div>}
      </div>
    </form>
  )
}
